import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FullName } from '@core/services/api-client.generated';
import { IActivityInfo } from '@app/project/project-admin/project-members/edit-entity-dialog/edit-entity-dialog.component';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { formatDateTime } from '@core/actions/util';
import { firstValueFrom, map, Observable } from 'rxjs';
import { IInfoConfirmationDialogData } from '@shared/dialogs/info-confirmation-dialog/info-confirmation-dialog.component';
import {
  SyrfDialogService,
  injectSyrfDialogRef,
} from '@shared/dialog/syrf-dialog.service';
import {
  SyrfDialogActionsDirective,
  SyrfDialogLayoutComponent,
} from '@shared/dialog/syrf-dialog-layout.component';
import { SyrfDialogCloseDirective } from '@shared/dialog/syrf-dialog-close.directive';
import { openInfoConfirmation } from '@shared/dialog/syrf-confirmations';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { IProjectAccessState } from '@core/actions/project-detail.actions';
import { ComponentStore } from '@ngrx/component-store';
import { IProject } from '@core/state/entities/project/project.entity';
import { IInvitation } from '@core/state/entities/invitation/invitation.entity';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { entityKey } from '@core/state/entities/entity-name-map';
import { selectCurrentUserId } from '@core/auth/auth.selectors';
import { FullNamePipe } from '@shared/pipes/full-name/full-name.pipe';
import { IEntityMap } from '@core/state/entities/entity';
import { IInvestigator } from '@core/state/entities/investigator/investigator.entity';
import { IJoinRequest } from '@core/state/entities/join-request/join-request.entity';
import { v4 as uuid } from 'uuid';
import { Router } from '@angular/router';
import { email } from 'ngrx-forms/validation';
import { authApiActions } from '@core/auth/auth.actions';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { InformationBoxComponent } from '../../../shared/information-box/information-box.component';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { DebuggerComponent } from '../../../shared/debugger/debugger.component';
import { DebuggerGroupComponent } from '../../../shared/debugger-group/debugger-group.component';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NewlinePipe } from '../../../shared/pipes/newline.pipe';

export const projectAccessApprovalRequiredText =
  'This is a project that requires new users to request to join.\nA project administrator will need to approve your request to join.';
export const projectAccessAutoApprovalText =
  'This is a project that does not require new users to request to join. You can join instantly.';
const autoApprovedMainMessage =
  'No admin approval is required to join this project.';
const projectAccessApprovalRequiredMainMessage =
  'You need to request to join this project.\nYour request will be sent to the project owner.';
const projectAccessPendingJoinRequestApprovalText =
  'Your request to join this project is pending administrator approval.';
const projectAccessInvitationPendingAcceptanceText =
  'You have been invited to join this project.';

export const getMainMessage = (dateTimeRequested: string): string =>
  `Your request is pending admin approval.\nYou requested to join this project on ${formatDateTime(
    dateTimeRequested
  )}.`;

export const getJoinProjectDialogMessages = (
  project: IProject,
  invitationMap: IEntityMap<IInvitation>,
  investigatorMap: IEntityMap<IInvestigator>,
  joinRequestMap: IEntityMap<IJoinRequest>
): {
  mainMessage: string;
  projectAccess: string;
  entityUserMessage?: string;
} =>
  project.invitation
    ? {
        mainMessage: `${FullNamePipe.transform(
          investigatorMap[invitationMap[project.invitation].invitedById]
            .fullName
        )} invited you to join this project on ${formatDateTime(
          invitationMap[project.invitation].dateTimeCreated
        )}.`,
        projectAccess: projectAccessInvitationPendingAcceptanceText,
        entityUserMessage: invitationMap[project.invitation].message,
      }
    : project.joinRequest
      ? {
          mainMessage: getMainMessage(
            joinRequestMap[project.joinRequest].dateTimeCreated
          ),
          projectAccess: projectAccessPendingJoinRequestApprovalText,
          entityUserMessage: joinRequestMap[project.joinRequest].message,
        }
      : project.autoApproveJoinRequests
        ? {
            mainMessage: autoApprovedMainMessage,
            projectAccess: projectAccessAutoApprovalText,
          }
        : {
            mainMessage: projectAccessApprovalRequiredMainMessage,
            projectAccess: projectAccessApprovalRequiredText,
          };
export const InvitationStatus = {
  // none: 'none',
  claimedByThisAccount: 'claimedByThisAccount',
  claimedByAnotherAccount: 'claimedByAnotherAccount',
  unclaimed: 'unclaimed',
} as const;
export type InvitationStatus =
  (typeof InvitationStatus)[keyof typeof InvitationStatus];
export const JoinProjectStatus = {
  initial: 'initial',
  inProgress: 'inProgress',
  pending: 'pending',
  success: 'success',
  error: 'error',
};
export const JoinProjectTerminalStates = [
  JoinProjectStatus.success,
  JoinProjectStatus.pending,
  JoinProjectStatus.error,
];
export type JoinProjectTerminalStates =
  | typeof JoinProjectStatus.success
  | typeof JoinProjectStatus.pending
  | typeof JoinProjectStatus.error;
export type JoinProjectStatus =
  (typeof JoinProjectStatus)[keyof typeof JoinProjectStatus];
export const JoinProjectState = {
  invited: 'invited',
  requestPending: 'requestPending',
  requestRequired: 'requestRequired',
  autoApproval: 'autoApproval',
} as const;
export type JoinProjectState =
  (typeof JoinProjectState)[keyof typeof JoinProjectState];
export interface IJoinProjectDialogData {
  joinProjectState: JoinProjectState;
  projectId: string;
  positiveActivity?: IActivityInfo<IProjectAccessState>;
  negativeActivity?: IActivityInfo<IProjectAccessState>;
  owner: FullName;
  projectAccess: string;
  description: string;
  mainMessage: string;
  projectName: string;
  dialogIcon?: string;
  recordReason?: boolean;
  invitationInfo?: { state: InvitationStatus; email: string };
  entityUserMessage?: string;
  onTerminalAction: () => void;
}
export interface IJoinProjectDialogState {
  joinProjectState: JoinProjectState;
  positiveActivity?: IActivityInfo<IProjectAccessState>;
  negativeActivity?: IActivityInfo<IProjectAccessState>;
  projectAccess: string;
  mainMessage: string;
  dialogIcon?: string;
  invitationInfo?: { state: InvitationStatus; email: string };
  joinProjectStatus: JoinProjectStatus;
  joinProjectError?: string;
  friendlyErrorMessage?: string;
  successMessage?: string;
  infoBoxMessage?: string;
  entityUserMessage?: string;
}

export const JoinProjectDialogResult = {
  joinProject: 'joinProject',
  autoClose: 'autoClose',
  dismiss: 'dismiss',
};
export type JoinProjectDialogResult =
  (typeof JoinProjectDialogResult)[keyof typeof JoinProjectDialogResult];

/**
 * Shows a project the user may join, request to join or accept an invitation to, and runs that action. Results:
 * Close and OK `'dismiss'`; a successful join `'joinProject'`; the opener's abort `'autoClose'`; Escape and the
 * backdrop `undefined`.
 */
@Component({
  selector: 'app-join-project-dialog',
  templateUrl: './join-project-dialog.component.html',
  styleUrls: ['./join-project-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SyrfDialogLayoutComponent,
    SyrfDialogActionsDirective,
    SyrfDialogCloseDirective,
    MatProgressBarModule,
    MatIconModule,
    DebuggerGroupComponent,
    DebuggerComponent,
    FlexLayoutModule,
    InformationBoxComponent,
    MatFormFieldModule,
    MatInputModule,
    TextFieldModule,
    FormsModule,
    MatButtonModule,
    AsyncPipe,
    FullNamePipe,
    NewlinePipe,
  ],
})
export class JoinProjectDialogComponent extends ComponentStore<IJoinProjectDialogState> {
  JoinProjectDialogResult = JoinProjectDialogResult;
  JoinProjectState = JoinProjectState;
  message: string;
  InvitationStatus = InvitationStatus;
  projectMap$ = inject(Store).select(selectEntityMap(entityKey.projects));
  investigatorMap$ = inject(Store).select(
    selectEntityMap(entityKey.investigators)
  );
  invitationMap$ = inject(Store).select(selectEntityMap(entityKey.invitations));
  joinRequestMap$ = inject(Store).select(
    selectEntityMap(entityKey.joinRequests)
  );
  currentUserId$ = inject(Store).select(selectCurrentUserId);
  inTerminalState$ = this.select((s) =>
    JoinProjectTerminalStates.includes(s.joinProjectStatus)
  );
  notInTerminalState$ = this.select(
    (s) => !JoinProjectTerminalStates.includes(s.joinProjectStatus)
  );
  inProgress$ = this.select(
    (s) => s.joinProjectStatus === JoinProjectStatus.inProgress
  );
  project$ = this.projectMap$.pipe(
    map((projectMap) => projectMap[this.data.projectId])
  );
  terminalStateInfoBoxMessage$: Observable<string | null> = this.select(
    (state) => {
      switch (state.joinProjectStatus) {
        case JoinProjectStatus.pending:
          return state.infoBoxMessage ?? null;
        default:
          return null;
      }
    }
  );
  terminalStateMessage$ = this.select((state) => {
    switch (state.joinProjectStatus) {
      case JoinProjectStatus.pending:
        return state.successMessage;
      case JoinProjectStatus.error:
        return state.friendlyErrorMessage;
      default:
        return undefined;
    }
  });
  inErrorState$ = this.select(
    (state) => state.joinProjectStatus === JoinProjectStatus.error
  );
  readonly data = inject<IJoinProjectDialogData>(MAT_DIALOG_DATA);
  private _dialogRef = injectSyrfDialogRef<
    JoinProjectDialogComponent,
    JoinProjectDialogResult
  >();
  private _dialog = inject(SyrfDialogService);
  private readonly _store = inject(Store);
  private readonly _actions = inject(Actions);
  private readonly _router = inject(Router);
  constructor() {
    // The same instance as `data` (field initialisers run after super()).
    const data = inject<IJoinProjectDialogData>(MAT_DIALOG_DATA);
    super({
      joinProjectState: data.joinProjectState,
      positiveActivity: data.positiveActivity,
      negativeActivity: data.negativeActivity,
      projectAccess: data.projectAccess,
      mainMessage: data.mainMessage,
      dialogIcon: data.dialogIcon,
      invitationInfo: data.invitationInfo,
      joinProjectStatus: JoinProjectStatus.initial,
      entityUserMessage: data.entityUserMessage,
    });
  }
  async action(
    activityInfo: IActivityInfo<IProjectAccessState>
  ): Promise<void> {
    const confirmationDialogData =
      activityInfo.confirmationDialog?.(false) ?? null;
    if (
      confirmationDialogData &&
      !(await firstValueFrom(
        openInfoConfirmation(this._dialog, confirmationDialogData).afterClosed()
      ))
    ) {
      return;
    }
    this.patchState({
      joinProjectStatus: JoinProjectStatus.inProgress,
    });
    this.data.onTerminalAction();
    this._store.dispatch(
      activityInfo.dispatchAction({
        message: this.data.recordReason ? this.message : undefined,
        correlationId: uuid(),
      })
    );
    const responseAction = await firstValueFrom(
      this._actions.pipe(
        ofType(
          ...[activityInfo.successActions, activityInfo.errorActions].flat(2)
        )
      )
    );
    const joinProjectStatus =
      'accessGranted' in responseAction
        ? responseAction.accessGranted
          ? JoinProjectStatus.success
          : JoinProjectStatus.pending
        : JoinProjectStatus.error;
    const partialStateUpdate: Partial<IJoinProjectDialogState> = {
      joinProjectStatus,
      ...getJoinProjectDialogMessages(
        await firstValueFrom(this.project$),
        await firstValueFrom(this.invitationMap$),
        await firstValueFrom(this.investigatorMap$),
        await firstValueFrom(this.joinRequestMap$)
      ),
      friendlyErrorMessage:
        joinProjectStatus === JoinProjectStatus.error
          ? activityInfo.friendlyErrorMessage
          : undefined,
      joinProjectError:
        'error' in responseAction ? responseAction.error : undefined,
      successMessage:
        joinProjectStatus !== JoinProjectStatus.error
          ? activityInfo.successMessage
          : undefined,
      infoBoxMessage:
        joinProjectStatus !== JoinProjectStatus.error
          ? activityInfo.infoBoxMessage
          : undefined,
    };
    this.patchState(partialStateUpdate);
    if (joinProjectStatus === JoinProjectStatus.success) {
      this._dialogRef.close(JoinProjectDialogResult.joinProject);
    }
  }

  signInWithCorrectAccount() {
    this._store.dispatch(
      authApiActions.startSignIn({
        login_hint: this.data.invitationInfo?.email,
      })
    );
  }

  async associateEmailAddress(emailHint?: string) {
    this._router.navigate(['/manage/profile', { emailHint }]);
  }
}
